import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, X, Check, Ruler, Edit2 } from 'lucide-react';
import { useAuth } from './AuthContext';
import { authFetch } from './api';

const API = 'https://hs.aida-advisory.com';

export default function CriteriaMeasurables() {
  const { user } = useAuth();
  const [measDefs, setMeasDefs] = useState([]);
  const [showAddMeas, setShowAddMeas] = useState(false);
  const [measForm, setMeasForm] = useState({ name: '', unit: '', description: '', position: 'ALL', target_operator: '<', target_value: '' });
  const [measError, setMeasError] = useState('');
  const [measSuccess, setMeasSuccess] = useState('');
  const [editingMeas, setEditingMeas] = useState(null);
  const [editMeasForm, setEditMeasForm] = useState({});

  const isAdmin = user?.role === 'super_admin' || user?.role === 'school_admin';

  const loadMeasDefs = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/measurables/definitions`);
      if (res.ok) setMeasDefs(await res.json());
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { loadMeasDefs(); }, [loadMeasDefs]);

  if (!isAdmin) return null;

  const handleAddMeas = async () => {
    setMeasError(''); setMeasSuccess('');
    if (!measForm.name.trim()) { setMeasError('Name is required'); return; }
    const payload = { ...measForm };
    if (payload.target_value === '' || payload.target_value === null) {
      payload.target_operator = null;
      payload.target_value = null;
    } else {
      payload.target_value = parseFloat(payload.target_value);
    }
    try {
      const res = await authFetch(`${API}/api/measurables/definitions`, { method: 'POST', body: JSON.stringify(payload) });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail); }
      setMeasSuccess('"' + measForm.name + '" created');
      setMeasForm({ name: '', unit: '', description: '', position: 'ALL', target_operator: '<', target_value: '' });
      setShowAddMeas(false); loadMeasDefs(); setTimeout(() => setMeasSuccess(''), 3000);
    } catch (err) { setMeasError(err.message); }
  };

  const handleEditMeas = async (id) => {
    setMeasError('');
    const payload = { ...editMeasForm };
    if (payload.target_value === '' || payload.target_value === null) {
      payload.target_operator = null;
      payload.target_value = null;
    } else {
      payload.target_value = parseFloat(payload.target_value);
    }
    try {
      const res = await authFetch(`${API}/api/measurables/definitions/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail); }
      setEditingMeas(null); setMeasSuccess('Updated'); loadMeasDefs(); setTimeout(() => setMeasSuccess(''), 3000);
    } catch (err) { setMeasError(err.message); }
  };

  const handleDeleteMeas = async (id, name) => {
    if (!window.confirm('Delete "' + name + '" and all recorded values across all players?')) return;
    try { await authFetch(`${API}/api/measurables/definitions/${id}`, { method: 'DELETE' }); loadMeasDefs(); } catch (err) { console.error(err); }
  };

  const startEditMeas = (m) => {
    setEditingMeas(m.id);
    setEditMeasForm({ name: m.name, unit: m.unit, description: m.description || '', position: m.position, target_operator: m.target_operator || '<', target_value: m.target_value ?? '' });
  };

  const allPos = ['ALL','QB','RB','FB','WR','TE','OT','OG','C','RE','PE','DT','DE','SAM','MIKE','WILL','LB','CB','S','FS','SS','K','P','LS','KR','PR'];

  const formatTarget = (m) => {
    if (!m.target_operator || m.target_value === null || m.target_value === undefined) return null;
    return `${m.target_operator} ${m.target_value}${m.unit ? ' ' + m.unit : ''}`;
  };

  return (
    <div className="mt-10 border-t border-[#546E7A]/30 pt-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2"><Ruler className="w-5 h-5 text-[#00D4FF]" />Custom Measurables</h2>
        <button onClick={() => { setShowAddMeas(!showAddMeas); setMeasError(''); }} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00D4FF]/10 border border-[#00D4FF]/40 text-[#00D4FF] rounded-lg text-sm hover:bg-[#00D4FF]/20 transition-colors"><Plus size={14} /> Add Measurable</button>
      </div>
      <p className="text-[#546E7A] text-sm mb-4">Define custom drills and measurements your program tracks. Set targets to generate a Measurables Score on the Fit Analysis page.</p>

      {measSuccess && <div className="bg-green-900/30 border border-green-700/50 text-green-300 px-3 py-2 rounded-lg mb-3 text-sm">{measSuccess}</div>}
      {measError && !showAddMeas && <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-3 py-2 rounded-lg mb-3 text-sm">{measError}</div>}

      {showAddMeas && (
        <div className="bg-[#1A2744] rounded-xl p-4 border border-[#546E7A]/30 mb-4">
          <h3 className="text-white font-semibold text-sm mb-3">New Custom Measurable</h3>
          {measError && <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-3 py-2 rounded-lg mb-3 text-xs">{measError}</div>}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div><label className="block text-xs font-medium text-gray-400 mb-1">Name</label><input type="text" value={measForm.name} onChange={(e) => setMeasForm({...measForm, name: e.target.value})} className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" placeholder="3-Cone Drill" /></div>
            <div><label className="block text-xs font-medium text-gray-400 mb-1">Unit</label><input type="text" value={measForm.unit} onChange={(e) => setMeasForm({...measForm, unit: e.target.value})} className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" placeholder="seconds" /></div>
            <div><label className="block text-xs font-medium text-gray-400 mb-1">Position</label><select value={measForm.position} onChange={(e) => setMeasForm({...measForm, position: e.target.value})} className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">{allPos.map(p => <option key={p} value={p}>{p === 'ALL' ? 'All Positions' : p}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-gray-400 mb-1">Description</label><input type="text" value={measForm.description} onChange={(e) => setMeasForm({...measForm, description: e.target.value})} className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" placeholder="Optional" /></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-400 mb-1">Target Threshold (optional — enables scoring)</label>
              <div className="flex gap-2">
                <select value={measForm.target_operator} onChange={(e) => setMeasForm({...measForm, target_operator: e.target.value})}
                  className="w-36 px-2 py-2 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                  <option value="<">&lt; Less than</option>
                  <option value=">">&gt; Greater than</option>
                  <option value="=">&equals; Equal to</option>
                </select>
                <input type="number" step="any" value={measForm.target_value} onChange={(e) => setMeasForm({...measForm, target_value: e.target.value})}
                  className="flex-1 px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                  placeholder="Target value" />
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleAddMeas} className="px-4 py-2 bg-[#00D4FF] text-[#0A0E27] rounded-lg font-semibold text-xs">Create</button>
            <button onClick={() => setShowAddMeas(false)} className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg text-xs">Cancel</button>
          </div>
        </div>
      )}

      {measDefs.length === 0 ? (
        <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-6 text-center">
          <Ruler className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">No custom measurables defined yet.</p>
          <p className="text-gray-500 text-xs mt-1">Create measurables here — they will appear on each player's Grading tab for coaches to record values.</p>
        </div>
      ) : (
        <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 overflow-hidden"><div className="divide-y divide-[#546E7A]/20">
          {measDefs.map(m => (
            <div key={m.id} className="px-4 py-3">
              {editingMeas === m.id ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div><label className="block text-xs text-gray-400 mb-1">Name</label><input type="text" value={editMeasForm.name} onChange={(e) => setEditMeasForm({...editMeasForm, name: e.target.value})} className="w-full px-3 py-1.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" /></div>
                    <div><label className="block text-xs text-gray-400 mb-1">Unit</label><input type="text" value={editMeasForm.unit} onChange={(e) => setEditMeasForm({...editMeasForm, unit: e.target.value})} className="w-full px-3 py-1.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" /></div>
                    <div><label className="block text-xs text-gray-400 mb-1">Position</label><select value={editMeasForm.position} onChange={(e) => setEditMeasForm({...editMeasForm, position: e.target.value})} className="w-full px-3 py-1.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">{allPos.map(p => <option key={p} value={p}>{p === 'ALL' ? 'All Positions' : p}</option>)}</select></div>
                    <div><label className="block text-xs text-gray-400 mb-1">Target</label>
                      <div className="flex gap-1">
                        <select value={editMeasForm.target_operator || '<'} onChange={(e) => setEditMeasForm({...editMeasForm, target_operator: e.target.value})} className="w-16 px-1 py-1.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                          <option value="<">&lt;</option><option value=">">&gt;</option><option value="=">=</option>
                        </select>
                        <input type="number" step="any" value={editMeasForm.target_value ?? ''} onChange={(e) => setEditMeasForm({...editMeasForm, target_value: e.target.value})} className="flex-1 px-2 py-1.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" placeholder="Target" />
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2"><button onClick={() => handleEditMeas(m.id)} className="p-1.5 text-green-400 hover:bg-green-900/30 rounded"><Check size={16} /></button><button onClick={() => setEditingMeas(null)} className="p-1.5 text-gray-400 hover:bg-gray-700 rounded"><X size={16} /></button></div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
                    <Ruler className="w-4 h-4 text-[#00D4FF] flex-shrink-0" />
                    <span className="text-white font-medium text-sm">{m.name}</span>
                    {m.unit && <span className="text-gray-500 text-xs">({m.unit})</span>}
                    {m.description && <span className="text-gray-500 text-xs italic">{m.description}</span>}
                    <span className={`px-1.5 py-0.5 rounded text-xs ${m.position === 'ALL' ? 'bg-[#00E676]/10 text-[#00E676]' : 'bg-[#00D4FF]/10 text-[#00D4FF]'}`}>{m.position === 'ALL' ? 'All Positions' : m.position}</span>
                    {formatTarget(m) && (
                      <span className="px-1.5 py-0.5 rounded text-xs bg-yellow-600/10 text-yellow-300 border border-yellow-600/20">
                        Target: {formatTarget(m)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => startEditMeas(m)} className="p-1.5 text-gray-400 hover:text-[#00D4FF] hover:bg-[#243B55] rounded"><Edit2 size={14} /></button>
                    <button onClick={() => handleDeleteMeas(m.id, m.name)} className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-[#243B55] rounded"><Trash2 size={14} /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div></div>
      )}
    </div>
  );
}
